import { Dispatch, SetStateAction } from 'react';
import { FieldData, ValidatorFunction } from '../store/models/models';

export const validateFirstName = (firstName: string) => {
  let isError = false;
  let firstNameError = '';

  if (firstName.trim().length < 1) {
    firstNameError = 'First name is required';
    isError = true;
  } else if (firstName.length > 30) {
    firstNameError = 'First name should not exceed 30 characters';
    isError = true;
  } else {
    const regex = /^[a-zA-Z\s'-]+$/;

    if (!regex.test(firstName)) {
      firstNameError = 'First name contains invalid characters';
      isError = true;
    }
  }

  return { isValid: !isError, errorMessage: firstNameError };
};

export const validateLastName = (lastName: string) => {
  let lastNameError = '';
  let isError = false;

  if (lastName.trim().length < 1) {
    lastNameError = 'Last name is required';
    isError = true;
  } else if (lastName.length > 30) {
    lastNameError = 'Last name should not exceed 30 characters';
    isError = true;
  } else {
    const regex = /^[a-zA-Z\s'-]+$/;

    if (!regex.test(lastName)) {
      lastNameError = 'Last name contains invalid characters';
      isError = true;
    }
  }

  return { isValid: !isError, errorMessage: lastNameError };
};

export const validateEmail = (email: string) => {
  let emailError = '';
  let isError = false;

  if (email.length > 40 || email.length < 3) {
    emailError = 'Email Address length should be between 3 and 40 characters';
    isError = true;
  }
  const regex = new RegExp(
    // eslint-disable-next-line no-control-regex
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
  );
  if (email.length > 0 && !regex.test(email)) {
    emailError = 'Email should have respective format';
    isError = true;
  }

  return { isValid: !isError, errorMessage: emailError };
};

export const validateName = (name: string) => {
  const regex = /^[A-Za-z]+$/;
  let nameError = '';
  let isError = false;

  if (name.length > 9 || name.length < 1) {
    nameError = 'Name length must be between 1 and 9 characters';
    isError = true;
  }

  if (!regex.test(name) && !isError) {
    nameError = 'Invalid characters: allowed only letters';
    isError = true;
  }

  return { isValid: !isError, errorMessage: nameError };
};

export const isIdValid = (vendorId: string) => vendorId?.length === 36;

export const validateCountry = (country: string) => {
  let countryError = '';
  let isError = false;

  if (country.length < 1) {
    countryError = 'Country is a required field';
    isError = true;
  }

  return { isValid: !isError, errorMessage: countryError };
};

export const validateState = (state: string) => {
  let stateError = '';
  let isError = false;

  if (state.length > 0 && (state.length > 20 || state.length < 1)) {
    stateError = 'If provided, State length should be between 1 and 20 characters';
    isError = true;
  }
  const regex = new RegExp(/^([a-zA-Z0-9\u0600-\u06FF\u0660-\u0669\u06F0-\u06F9 _.-]+)$/);
  if (state.length > 0 && !regex.test(state)) {
    stateError = 'State should be alphanumeric';
    isError = true;
  }

  return { isValid: !isError, errorMessage: stateError };
};

export const validateZipCode = (zipCode: string) => {
  let zipCodeError = '';
  let isError = false;

  if (zipCode.length > 0 && (zipCode.length > 10 || zipCode.length < 4)) {
    zipCodeError = 'If provided, Zip Code length should be between 4 and 10 characters';
    isError = true;
  }
  const regex = new RegExp(/^([a-zA-Z0-9\u0600-\u06FF\u0660-\u0669\u06F0-\u06F9 _.-]+)$/);
  if (zipCode.length > 0 && !regex.test(zipCode)) {
    zipCodeError = 'Zip Code should be alphanumeric';
    isError = true;
  }

  return { isValid: !isError, errorMessage: zipCodeError };
};

export const validateStreetAddress = (streetAddress: string) => {
  let streetAddressError = '';
  let isError = false;

  if (streetAddress.length > 40 || streetAddress.length < 3) {
    streetAddressError = 'Street Address length should be between 3 and 40 characters';
    isError = true;
  }
  const regex = new RegExp(/^([a-zA-Z0-9\u0600-\u06FF\u0660-\u0669\u06F0-\u06F9 _.-]+)$/);
  if (streetAddress.length > 0 && !regex.test(streetAddress)) {
    streetAddressError = 'Street Address should be alphanumeric';
    isError = true;
  }

  return { isValid: !isError, errorMessage: streetAddressError };
};

export const validateCity = (city: string) => {
  let cityError = '';
  let isError = false;

  if (city.length > 20 || city.length < 1) {
    cityError = 'City length should be between 1 and 20 characters';
    isError = true;
  }
  const regex = new RegExp(/^([a-zA-Z0-9\u0600-\u06FF\u0660-\u0669\u06F0-\u06F9 _.-]+)$/);
  if (city.length > 0 && !regex.test(city)) {
    cityError = 'City should be alphanumeric';
    isError = true;
  }

  return { isValid: !isError, errorMessage: cityError };
};

export const validatePhoneNumber = (phoneNumber: string) => {
  let phoneNumberError = '';
  let isError = false;

  if (phoneNumber.length > 20 || (phoneNumber.length > 0 && phoneNumber.length < 7)) {
    phoneNumberError = 'Phone Number length should be between 7 and 20 characters';
    isError = true;
  }
  // If phone number is provided, validate its characters.
  if (phoneNumber && phoneNumber.trim().length > 0) {
    const regex = /^[0-9+\-() ]+$/;
    if (!regex.test(phoneNumber)) {
      phoneNumberError = 'Phone number contains invalid characters';
      isError = true;
    }
  }

  return { isValid: !isError, errorMessage: phoneNumberError };
};

export const validateAgreedFlag = (agreedFlag: boolean) => {
  let agreedFlagError = '';
  let isError = false;

  if (!agreedFlag) {
    agreedFlagError = 'Vendor agreement is required';
    isError = true;
  }

  return { isValid: !isError, errorMessage: agreedFlagError };
};

export const isValidField = (
  value: string,
  setStateCb: Dispatch<SetStateAction<FieldData>>,
  validator: ValidatorFunction,
) => {
  const { isValid, errorMessage } = validator(value);

  setStateCb((prevState) => ({ ...prevState, isError: !isValid, errorMessage }));

  return isValid;
};
